
import React from 'react';
import { QueryMode, ensureIsArray, t, validateNonEmpty } from '@superset-ui/core';
import {
    ControlPanelConfig,
    sections,
    sharedControls,
    ControlStateMapping,
    emitFilterControl,
    ControlPanelsContainerProps,
    ColumnOption,
} from '@superset-ui/chart-controls';
import { DEFAULT_FORM_DATA } from './types';
import { legendSection } from '../../../controls';
import { commonControlsConfig } from '../utils';

const { showLabels } = DEFAULT_FORM_DATA;

const chartLegendSection = [...legendSection];
chartLegendSection.splice(2, 1);

const all_columns: typeof sharedControls.groupby = {
  type: 'SelectControl',
  label: t('Columns'),
  description: t('Columns to display'),
  multi: true,
  freeForm: true,
  allowAll: true,
  commaChoosesOption: false,
  default: [],
  optionRenderer: c => <ColumnOption showType column={c} />,
  valueRenderer: c => <ColumnOption column={c} />,
  valueKey: 'column_name',
  mapStateToProps: ({ datasource, controls }, controlState) => ({
    options: datasource?.columns || [],
    queryMode: QueryMode.raw,
    externalValidationErrors:
      controls?.select_view_as_table_columns?.value === true && ensureIsArray(controlState.value).length === 0
      ? [t('must have a value')]
      : []
  }),
  visibility: ({ controls }) => Boolean(controls?.select_view_as_table_columns?.value),
  resetOnHide: false,
};

const config: ControlPanelConfig = {
   controlPanelSections: [
    {
      ...sections.legacyRegularTime,
      expanded: false
    },
     {
       label: t('Query'),
       expanded: true,
       controlSetRows: [
            [
              {
                name: 'enable_drill_down',
                config: {
                  type: 'CheckboxControl',
                  label: t('Enable DrillDown'),
                  renderTrigger: false,
                  default: DEFAULT_FORM_DATA.enableDrillDown,
                  description: t('To enable selection of multiple columns for DrillDown Query.'),
                },
              },
            ],
            [
              {
                name: 'groupby',
                config: {
                  ...sharedControls.groupby,
                  multi: false,
                  clearable: false,
                  validators: [validateNonEmpty]
                }
              }
            ],
            [
              {
                name: 'drill_down_groupby',
                config: {
                  ...sharedControls.groupby,
                  multi: true,
                  label: t('Drilldown Columns'),
                  description: t('Column values to use as categories for each level.'),
                  visibility: ({ controls }: ControlPanelsContainerProps) =>
                    Boolean(controls?.enable_drill_down?.value),
                },
              },
            ],
            ['metric'],
            ['adhoc_filters'],
            emitFilterControl,
            [
            {
                name: 'row_limit',
                config: {
                ...sharedControls.row_limit,
                default: 10,
                },
            },
            ],
            [
              {
                name: 'select_view_as_table_columns',
                config: {
                  default: false,
                  type: 'CheckboxControl',
                  label: t('Select \'View as Table\' Columns'),
                  description: t(
                    'Select custom columns for the \'View as table\' select query.',
                  ),
                },
              },
            ],
            [
              {
                name: 'view_as_table_columns',
                config: all_columns
              },
            ]
       ],
     },
     {
       label: t('Chart Options'),
       tabOverride: 'customize',
       expanded: false,
       controlSetRows: [
        //  ['color_scheme'],
         ...chartLegendSection,
         // eslint-disable-next-line react/jsx-key
         [<div className="section-header">{t('Labels')}</div>],
         [
           {
             name: 'show_labels',
             config: {
               type: 'CheckboxControl',
               label: t('Show Labels'),
               renderTrigger: true,
               default: showLabels,
               description: t('Whether to display the labels.'),
             },
           },
         ],

         [
          {
            name: 'tooltipFormat',
            config: {
              ...commonControlsConfig.tooltipFormat,
              default: DEFAULT_FORM_DATA.tooltipFormat,
            },
          }
         ],
         [
          {
            name: 'labelPosition',
            config: {
              ...commonControlsConfig.labelPosition,
              default: DEFAULT_FORM_DATA.labelPosition,
            },
          }
        ],
        [
          {
            name: 'labelAlignment',
            config: {
              ...commonControlsConfig.labelAlignment,
              default: DEFAULT_FORM_DATA.labelAlignment,
            },
          }
        ],
        [
          {
            name: 'enableFiltering',
            config: {
              type: 'CheckboxControl',
              label: t('Enable Filtering'),
              renderTrigger: true,
              default: false,
              description: t('Whether to apply a filter on selecting a slice.'),
            },
          },
        ],
       ],
     },
   ],
   onInit(state: ControlStateMapping) {
     return {
       ...state,
       row_limit: {
         ...state.row_limit,
         value: state.row_limit.default,
       },
     };
   },
   denormalizeFormData: formData => ({
     ...formData,
     metric: formData.standardizedFormData.standardizedState.metrics[0],
     groupby: formData.standardizedFormData.standardizedState.columns,
   }),
   updateStandardizedState: (prevState, currState) => ({
     ...currState,
     metrics: [currState.metrics[0], ...prevState.metrics.slice(1)],
   }),
};
 
export default config;
 